import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Paper,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Person from "@mui/icons-material/Person";
import Block from "@mui/icons-material/Block";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import Email from "@mui/icons-material/Email";
import Phone from "@mui/icons-material/Phone";
import Badge from "@mui/icons-material/Badge";
import VerifiedUser from "@mui/icons-material/VerifiedUser";
import LockReset from "@mui/icons-material/LockReset";
import Business from "@mui/icons-material/Business";
import Store from "@mui/icons-material/Store";
import {
  useListSuperAdmins,
  useUpdateSuperAdminStatus,
  useUpdateSuperAdminRole,
} from "../../hooks/api/useAdminUsers";
import type { SuperAdminUser } from "../../hooks/api/useAdminUsers";
import { ROUTE_ADMIN_USERS } from "../../constants/routes";
import { responsiveHeading } from "../../styles/layoutStyles";
import { formatRoleForDisplay } from "../../Graphql/types/auth";
import { formatAccountStatusLabel } from "../common/adminEnumLabels";
import { useAdminAuth } from "../../context/AdminAuthContext";

function DetailRow({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string | null | undefined;
  icon?: React.ElementType;
}) {
  const display = value != null && value !== "" ? value : "—";
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      sx={{
        py: 1.25,
        "&:not(:last-of-type)": {
          borderBottom: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      {Icon && (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ fontSize: 18, color: "text.secondary" }} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 0.25 }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, wordBreak: "break-word" }}
        >
          {display}
        </Typography>
      </Box>
    </Stack>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Paper
      elevation={1}
      sx={{
        p: 2.5,
        borderRadius: 2,
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: "primary.main",
          letterSpacing: 0.5,
          mb: 2,
          pb: 1,
          borderBottom: "2px solid",
          borderColor: "primary.main",
          display: "inline-block",
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

const SUPER_ADMIN_ROLES = [
  { value: "PRIMARY_SUPER_ADMIN", label: "Primary Super Admin" },
  { value: "SECONDARY_SUPER_ADMIN", label: "Secondary Super Admin" },
] as const;

const AdminUserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAdminAuth();
  const [user, setUser] = useState<SuperAdminUser | null>(null);
  const [changeRoleOpen, setChangeRoleOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>(
    "SECONDARY_SUPER_ADMIN",
  );

  /** Only primary super admin can change another user's role (backend enforces; we hide for secondary). */
  const canChangeRole = currentUser?.role === "PRIMARY_SUPER_ADMIN";

  const listSuperAdmins = useListSuperAdmins();
  const updateStatus = useUpdateSuperAdminStatus();
  const updateRole = useUpdateSuperAdminRole();

  useEffect(() => {
    if (!userId) return;
    listSuperAdmins
      .mutateAsync()
      .then((res) => {
        const list = res.listSuperAdmins?.users ?? [];
        const found = list.find((u) => u.id === userId) ?? null;
        setUser(found);
        if (found?.role) setSelectedRole(found.role);
      })
      .catch(() => {});
  }, [userId]);

  const handleDeactivate = () => {
    if (!userId || !user) return;
    updateStatus
      .mutateAsync({ userId, status: "SUSPENDED" })
      .then((res) => {
        const u = res.updateSuperAdminStatus?.user;
        if (u) setUser(u);
      })
      .catch(() => {});
  };

  const handleActivate = () => {
    if (!userId || !user) return;
    updateStatus
      .mutateAsync({ userId, status: "ACTIVE" })
      .then((res) => {
        const u = res.updateSuperAdminStatus?.user;
        if (u) setUser(u);
      })
      .catch(() => {});
  };

  const handleChangeRoleOpen = () => {
    if (user?.role) setSelectedRole(user.role);
    setChangeRoleOpen(true);
  };

  const handleChangeRoleSubmit = () => {
    if (!userId || selectedRole === user?.role) {
      setChangeRoleOpen(false);
      return;
    }
    updateRole
      .mutateAsync({ userId, role: selectedRole })
      .then((res) => {
        const u = res.updateSuperAdminRole?.user;
        if (u) setUser(u);
        setChangeRoleOpen(false);
      })
      .catch(() => {});
  };

  const isLoading = listSuperAdmins.isPending && !user;
  const notFound = !isLoading && !user && userId;

  if (notFound) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography color="text.secondary">User not found.</Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(ROUTE_ADMIN_USERS)}
          sx={{ mt: 2 }}
        >
          Back to list
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }} data-testid="admin-user-detail-page">
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(ROUTE_ADMIN_USERS)}
        sx={{ mb: 2 }}
      >
        Back to Admin Users
      </Button>

      <Box
        sx={{
          width: { xs: "100%", md: "80%" },
          maxWidth: 960,
          p: { xs: 1.5, sm: 2, md: 3 },
          mx: "auto",
          boxSizing: "border-box",
        }}
      >
        {isLoading ? (
          <>
            <Skeleton variant="circular" width={72} height={72} />
            <Skeleton variant="text" width="40%" height={32} sx={{ mt: 2 }} />
            <Skeleton variant="text" width="60%" height={24} sx={{ mt: 0.5 }} />
            <Skeleton variant="rectangular" height={120} sx={{ mt: 2 }} />
          </>
        ) : user ? (
          <Stack spacing={3.5}>
            {/* Header strip: avatar, name, chips, and actions */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "grey.50",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "stretch", md: "center" }}
                spacing={2}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {user.profileImage ? (
                      <Box
                        component="img"
                        src={user.profileImage}
                        alt=""
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Person sx={{ fontSize: 36 }} />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, ...responsiveHeading }}
                    >
                      {[user.firstName, user.lastName]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25, wordBreak: "break-word" }}
                    >
                      {user.email ?? "—"}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{ mt: 1.5 }}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      {user.role && (
                        <Chip
                          size="small"
                          label={formatRoleForDisplay(user.role)}
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                      {user.status && (
                        <Chip
                          size="small"
                          label={formatAccountStatusLabel(user.status)}
                          color={
                            user.status === "ACTIVE" ? "success" : "default"
                          }
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                    </Stack>
                  </Box>
                </Box>
                <Stack
                  direction="row"
                  spacing={1.5}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{
                    flexShrink: 0,
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                  }}
                >
                  {user.status === "ACTIVE" ? (
                    <Button
                      variant="outlined"
                      color="error"
                      size="medium"
                      startIcon={<Block />}
                      onClick={handleDeactivate}
                      disabled={updateStatus.isPending}
                    >
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="medium"
                      onClick={handleActivate}
                      disabled={updateStatus.isPending}
                    >
                      Activate
                    </Button>
                  )}
                  {canChangeRole && (
                    <Button
                      variant="outlined"
                      size="medium"
                      startIcon={<SwapHoriz />}
                      onClick={handleChangeRoleOpen}
                      disabled={updateRole.isPending}
                    >
                      Change role
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>

            {/* Two cards per row: Profile | Account, then Scope full width */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
              }}
            >
              <Box
                sx={{
                  flex: { xs: "0 0 100%", md: "0 0 calc(50% - 12px)" },
                  minWidth: 0,
                }}
              >
                <SectionCard title="Profile">
                  <Stack>
                    <DetailRow
                      label="First name"
                      value={user.firstName ?? null}
                      icon={Person}
                    />
                    <DetailRow
                      label="Last name"
                      value={user.lastName ?? null}
                      icon={Person}
                    />
                    <DetailRow
                      label="Email"
                      value={user.email ?? null}
                      icon={Email}
                    />
                    <DetailRow
                      label="Phone"
                      value={user.phone ?? null}
                      icon={Phone}
                    />
                  </Stack>
                </SectionCard>
              </Box>
              <Box
                sx={{
                  flex: { xs: "0 0 100%", md: "0 0 calc(50% - 12px)" },
                  minWidth: 0,
                }}
              >
                <SectionCard title="Account">
                  <Stack>
                    <DetailRow label="User ID" value={user.id} icon={Badge} />
                    <DetailRow
                      label="Role"
                      value={user.role ? formatRoleForDisplay(user.role) : null}
                    />
                    <DetailRow
                      label="Status"
                      value={formatAccountStatusLabel(user.status)}
                    />
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={2}
                      sx={{
                        py: 1.25,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: "action.hover",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <VerifiedUser
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 0.25 }}
                        >
                          Email verified
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.emailVerified === true
                            ? "Yes"
                            : user.emailVerified === false
                              ? "No"
                              : "—"}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={2}
                      sx={{ py: 1.25 }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: "action.hover",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <LockReset
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 0.25 }}
                        >
                          Password change required
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.passwordChangeRequired === true
                            ? "Yes"
                            : user.passwordChangeRequired === false
                              ? "No"
                              : "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                </SectionCard>
              </Box>
              {(user.companyId || user.storeId) && (
                <Box sx={{ flex: "0 0 100%", minWidth: 0 }}>
                  <SectionCard title="Scope">
                    <Stack>
                      <DetailRow
                        label="Company ID"
                        value={user.companyId ?? null}
                        icon={Business}
                      />
                      <DetailRow
                        label="Store ID"
                        value={user.storeId ?? null}
                        icon={Store}
                      />
                    </Stack>
                  </SectionCard>
                </Box>
              )}
            </Box>
          </Stack>
        ) : null}
      </Box>

      <Dialog
        open={changeRoleOpen}
        onClose={() => setChangeRoleOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change role</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="change-role-label">Role</InputLabel>
            <Select
              labelId="change-role-label"
              value={selectedRole}
              label="Role"
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              {SUPER_ADMIN_ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeRoleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleChangeRoleSubmit}
            disabled={updateRole.isPending || selectedRole === user?.role}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserDetailPage;
